import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { CheckInData, GuestIdDocument, BookingData } from '../interfaces/booking.interface';

export interface IdScanResult {
  success: boolean;
  message: string;
  extractedData?: {
    firstName?: string;
    lastName?: string;
    documentNumber?: string;
    expiryDate?: Date;
    dateOfBirth?: Date;
    nationality?: string;
    gender?: string;
    address?: string;
  };
  confidence?: number;
}

export interface CheckInStep {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CheckInService {
  private checkInDataSubject = new BehaviorSubject<CheckInData | null>(null);
  private currentStepSubject = new BehaviorSubject<number>(1);

  checkInData$ = this.checkInDataSubject.asObservable();
  currentStep$ = this.currentStepSubject.asObservable();

  private checkInSteps: CheckInStep[] = [
    { id: 1, title: 'Booking Verification', description: 'Verify your booking details', completed: false, active: true },
    { id: 2, title: 'ID Scanning', description: 'Scan your identification document', completed: false, active: false },
    { id: 3, title: 'Guest Information', description: 'Confirm guest details', completed: false, active: false },
    { id: 4, title: 'Access Code', description: 'Receive your room access code', completed: false, active: false }
  ];

  // Mock booking data for demonstration
  private mockBookings: Partial<BookingData>[] = [
    {
      bookingId: 'qweewq123',
      guestDetails: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'nikibala1992@gmail.com',
        phone: '+1234567890',
        emailVerified: true
      },
      dateRange: {
        start: new Date('2024-07-01'),
        end: new Date('2024-07-05')
      },
      status: 'confirmed'
    }
  ];

  constructor() {}

  getCheckInSteps(): CheckInStep[] {
    return [...this.checkInSteps];
  }

  getCurrentStep(): number {
    return this.currentStepSubject.value;
  }

  setCurrentStep(step: number): void {
    this.currentStepSubject.next(step);
    this.updateStepStatus(step);
  }

  nextStep(): void {
    const currentStep = this.currentStepSubject.value;
    if (currentStep < this.checkInSteps.length) {
      this.setCurrentStep(currentStep + 1);
    }
  }

  previousStep(): void {
    const currentStep = this.currentStepSubject.value;
    if (currentStep > 1) {
      this.setCurrentStep(currentStep - 1);
    }
  }

  private updateStepStatus(activeStep: number): void {
    this.checkInSteps.forEach((step, index) => {
      step.active = step.id === activeStep;
      step.completed = step.id < activeStep;
    });
  }

  verifyBooking(bookingId: string, email: string): Observable<{ success: boolean; booking?: Partial<BookingData>; message: string }> {
    // Simulate API call
    return of(null).pipe(
      delay(1500),
      map(() => {
        const booking = this.mockBookings.find(b =>
          b.bookingId === bookingId &&
          b.guestDetails?.email?.toLowerCase() === email.toLowerCase()
        );

        if (booking) {
          // Initialize check-in data
          const checkInData: CheckInData = {
            bookingId: bookingId,
            guestId: booking.guestDetails?.email || '',
            checkInStatus: 'not_started',
            idDocuments: [],
            createdAt: new Date(),
            updatedAt: new Date()
          };

          this.checkInDataSubject.next(checkInData);

          return {
            success: true,
            booking: booking,
            message: 'Booking verified successfully'
          };
        } else {
          return {
            success: false,
            message: 'Booking not found. Please check your booking ID and email address.'
          };
        }
      })
    );
  }

  scanIdDocument(imageFile: File, documentType: 'passport' | 'national_id' = 'passport'): Observable<IdScanResult> {
    // Simulate OCR processing with enhanced extraction
    return of(null).pipe(
      delay(3000),
      map(() => {
        // Enhanced mock OCR results based on document type
        const mockResults: IdScanResult = {
          success: true,
          message: 'ID document scanned successfully',
          extractedData: documentType === 'passport' ? {
            firstName: 'John',
            lastName: 'Doe',
            documentNumber: 'P123456789',
            expiryDate: new Date('2030-12-31'),
            dateOfBirth: new Date('1990-01-15'),
            nationality: 'US',
            gender: 'M',
            address: 'New York, NY, USA'
          } : {
            firstName: 'Jane',
            lastName: 'Smith',
            documentNumber: 'ID987654321',
            expiryDate: new Date('2028-06-20'),
            dateOfBirth: new Date('1985-06-20'),
            nationality: 'US',
            gender: 'F',
            address: '123 Main St, Los Angeles, CA'
          },
          confidence: 0.95
        };

        // Store the scanned document
        const currentCheckIn = this.checkInDataSubject.value;
        if (currentCheckIn) {
          const idDocument: GuestIdDocument = {
            id: 'doc_' + Date.now(),
            type: documentType,
            documentNumber: mockResults.extractedData?.documentNumber || '',
            expiryDate: mockResults.extractedData?.expiryDate || new Date(),
            issuingCountry: mockResults.extractedData?.nationality || '',
            extractedData: mockResults.extractedData,
            verificationStatus: 'verified',
            scannedAt: new Date(),
            scannedImageUrl: URL.createObjectURL(imageFile)
          };

          const updatedCheckIn: CheckInData = {
            ...currentCheckIn,
            checkInStatus: 'id_scanned',
            idDocuments: [...currentCheckIn.idDocuments, idDocument],
            updatedAt: new Date()
          };

          this.checkInDataSubject.next(updatedCheckIn);
        }

        return mockResults;
      })
    );
  }

  generateAccessCode(): Observable<{ success: boolean; qrCodeData?: any; message: string }> {
    return of(null).pipe(
      delay(2000),
      map(() => {
        const currentCheckIn = this.checkInDataSubject.value;
        if (!currentCheckIn) {
          return {
            success: false,
            message: 'No check-in data found'
          };
        }

        const qrCodeData = {
          code: 'QR_' + Date.now(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
          lockCode: '1234',
          accessInstructions: 'Use this QR code to access your room. The code will be active 2 hours before check-in time.'
        };

        const updatedCheckIn: CheckInData = {
          ...currentCheckIn,
          checkInStatus: 'completed',
          qrCodeGenerated: true,
          qrCodeData: qrCodeData,
          checkInTime: new Date(),
          updatedAt: new Date()
        };

        this.checkInDataSubject.next(updatedCheckIn);

        return {
          success: true,
          qrCodeData: qrCodeData,
          message: 'Access code generated successfully'
        };
      })
    );
  }

  completeCheckIn(): void {
    const currentCheckIn = this.checkInDataSubject.value;
    if (currentCheckIn) {
      const updatedCheckIn: CheckInData = {
        ...currentCheckIn,
        checkInStatus: 'completed',
        updatedAt: new Date()
      };
      this.checkInDataSubject.next(updatedCheckIn);
    }
  }

  resetCheckIn(): void {
    this.checkInDataSubject.next(null);
    this.setCurrentStep(1);
  }

  getCurrentCheckInData(): CheckInData | null {
    return this.checkInDataSubject.value;
  }

  updateCheckInData(checkInData: CheckInData): void {
    this.checkInDataSubject.next(checkInData);
  }
}
